import { Upload } from "lucide-react"
import { useEffect, useState } from "react"

import { ContentObjectItem } from "@vertesia/common"

import { UploadDocumentsButton } from "@/components/buttons/UploadDocumentsButton"
import { DocumentDataTable } from "@/components/datatables/DocumentDataTable"
import { Button } from "@ui/button"
import { useUserSession } from "@session/UserSession"

const TYPE = "66c752b63ec7eb95009bf89e"
const REVIEWED_TYPE = "66c752ef3ec7eb95009bf8b0"

const PAGE_SIZE = 200

export function ApplicationsPage() {
    const { client, isLoading } = useUserSession()

    const [documents, setDocuments] = useState<ContentObjectItem[]>([])
    const [reviewedDocuments, setReviewedDocuments] = useState<ContentObjectItem[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hasMoreDocuments, setHasMoreDocuments] = useState(true)
    const [currentPage, setCurrentPage] = useState(0)

    const searchObjects = async (loadMore: boolean = false) => {
        if (loadMore) {
            setIsLoadingMore(true)
        } else {
            setIsSearching(true)
            setCurrentPage(0)
            setDocuments([])
            setHasMoreDocuments(true)
        }

        const page = loadMore ? currentPage + 1 : 0
        const offset = page * PAGE_SIZE

        const query = {
            type: TYPE
        } as any

        client.objects.search({ limit: PAGE_SIZE, offset: offset, query })
            .then((response) => {
                const allDocuments = loadMore ? [...documents, ...response.results] : response.results
                for (const document of allDocuments) {
                    if (document.status !== "completed") {
                        document.name = `Processing ${document.name}`
                    } else if (document.properties?.title) {
                        document.name = document.properties.title
                    }
                }
                setDocuments(allDocuments)
                setCurrentPage(page)
                setHasMoreDocuments(response.results.length === PAGE_SIZE)

                if (!loadMore) {
                    const reviewedQuery = {
                        type: REVIEWED_TYPE
                    }
                    client.objects.search({ limit: PAGE_SIZE, offset: 0, query: reviewedQuery }).then((response) => {
                        setReviewedDocuments(response.results)
                    })
                }
            })
            .finally(() => {
                setIsLoadingMore(false)
                setIsSearching(false)
            })
    }

    const loadMoreDocuments = async () => {
        if (isLoadingMore || !hasMoreDocuments) {
            return false
        }

        await searchObjects(true)
        return hasMoreDocuments
    }

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        searchObjects()
    }, [client, isLoading])

    useEffect(() => {
        if (!reviewedDocuments?.length) {
            return
        }

        for (const document of documents) {
            if (!document.properties) {
                document.properties = {}
            }

            const reviewedDocument = reviewedDocuments.find((reviewedDocument) => reviewedDocument?.properties?.title?.replace(/ /g, "") === document.properties?.title?.replace(/ /g, ""))

            if (!reviewedDocument) {
                document.properties.score = -1
            } else {
                document.properties.score = reviewedDocument?.properties?.overall_impact?.score
            }
        }
    }, [documents, reviewedDocuments])

    return (
        <div className="h-full w-full flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-gray-300 p-4 bg-gray-50">
                <div className='flex flex-col gap-0'>
                    <span className="text-lg font-medium">Application Dashboard</span>
                    <span className="text-sm text-gray-600">Upload and view all your applications</span>
                </div>
                <UploadDocumentsButton onUploadDone={() => searchObjects()} />
            </div>
            <div className="flex-grow w-full overflow-auto p-4 bg-gray-100">
                <DocumentDataTable data={documents} loading={isSearching} loadingMore={isLoadingMore} hasMore={hasMoreDocuments} loadMore={loadMoreDocuments} />
            </div>
        </div>
    )
}